import type { TOC } from '@ember/component/template-only';

/**
 * Signature for the DefinitionTerm component.
 * This component represents a definition term (`<dt>`) with specific styling.
 *
 * @block default - The default block for yielding content inside the term.
 */
interface DefinitionTermSignature {
  Blocks: {
    default: [];
  };
  Element: HTMLElement;
}

/**
 * Definition Term Component
 *
 * This component is a definition term (`<dt>`) with specific styling.
 *
 * @example
 * ```gts
 * <DefinitionTerm>
 *   Term
 * </DefinitionTerm>
 * ```
 */
const DefinitionTerm: TOC<DefinitionTermSignature> = <template>
  <dt class={{DefinitionListOptions.dtClass}} ...attributes>{{yield}}</dt>
</template>;
/**
 * Signature for the DefinitionDescription component.
 * This component represents a definition description (`<dd>`) with specific styling.
 *
 * @block default - The default block for yielding content inside the description.
 */
interface DefinitionDescriptionSignature {
  Blocks: {
    default: [];
  };
  Element: HTMLElement;
}

/**
 * Definition Description Component
 *
 * This component is a definition description (`<dd>`) with specific styling.
 *
 * @example
 * ```gts
 * <DefinitionDescription>
 *   Description
 * </DefinitionDescription>
 * ```
 */
const DefinitionDescription: TOC<DefinitionDescriptionSignature> = <template>
  <dd class={{DefinitionListOptions.ddClass}} ...attributes>
    <div class="font-medium text-right">
      {{yield}}
    </div>
  </dd>
</template>;

/**
 * Configuration options for definition list components.
 */
const DefinitionListOptions = {
  dlClass: '-my-3 divide-y tio-divide-subtle text-sm leading-6',
  dtClass: 'tio-fg-muted',
  ddClass: 'flex items-start gap-x-2',
  // TODO: Can this be an option for the component? This determines stacked
  // or inline terms and descriptions.
  wrappingClass: 'flex justify-between gap-x-4 py-4',
  dd: DefinitionDescription,
  dt: DefinitionTerm,
};

/**
 * Signature for the DefinitionList component.
 * This component represents an entire definition list with configurable term and definition styling.
 *
 * @block default - Yields the DefinitionListOptions for configuring the definition list.
 */
interface DefinitionListSignature {
  Blocks: {
    default: [typeof DefinitionListOptions];
  };
  Element: HTMLDListElement;
}

/**
 * Definition List Component
 *
 * This component represents an entire definition list (`<dl>`) with terms (`<dt>`) and descriptions (`<dd>`).
 * It allows customization of terms and descriptions through blocks.
 *
 * @example
 * ```gts
 * <DefinitionList>
 *   <:default as |listOptions|>
 *     <listOptions.dt>
 *       Term 1
 *     </listOptions.dt>
 *     <listOptions.dd>
 *       Description 1
 *     </listOptions.dd>
 *     <listOptions.dt>
 *       Term 2
 *     </listOptions.dt>
 *     <listOptions.dd>
 *       Description 2
 *     </listOptions.dd>
 *   </:default>
 * </DefinitionList>
 * ```
 */
const DefinitionList: TOC<DefinitionListSignature> = <template>
  <dl class={{DefinitionListOptions.dlClass}} ...attributes>
    {{yield DefinitionListOptions}}
  </dl>
</template>;

export default DefinitionList;
